/**
 * Geographic Demand Page
 * Extracted from App.js - Phase 7B Frontend Refactor
 */
import { useState, useEffect, useCallback } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import MapCenterUpdater from "../components/demand/MapCenterUpdater";
import { MapPin, ChevronDown, Activity, BarChart3, ShieldCheck } from "lucide-react";

/* Styled select wrapper for GeoDemand filters */
const StyledSelect = ({ value, onChange, children, testId }) => (
  <div style={{ position: 'relative', display: 'inline-block' }}>
    <select
      value={value}
      onChange={onChange}
      data-testid={testId}
      style={{
        // iter83 — Bug 1: stop hiding the native select dropdown. The
        // dealer reported all <select>s on GeoDemand/Dashboard/Popular
        // pages would not open. The fix is to leave `appearance: auto`
        // (the browser default) so the native picker opens on click.
        // We keep the custom ChevronDown below the native one — most
        // browsers only render one arrow; on Firefox both may appear,
        // which is an acceptable cosmetic tradeoff vs. broken UX.
        appearance: 'auto', WebkitAppearance: 'auto',
        padding: '8px 32px 8px 12px',
        border: '1.5px solid #E5E7EB', borderRadius: '8px',
        background: 'white', fontSize: '13px', fontWeight: '500',
        color: '#374151', cursor: 'pointer',
        transition: 'border-color 0.15s',
        outline: 'none',
      }}
      onFocus={e => e.target.style.borderColor = '#005CFF'}
      onBlur={e => e.target.style.borderColor = '#E5E7EB'}
    >
      {children}
    </select>
    <ChevronDown size={14} style={{
      position: 'absolute', right: '10px', top: '50%',
      transform: 'translateY(-50%)', pointerEvents: 'none', color: '#6B7280'
    }} />
  </div>
);

const GeoDemandPage = ({ onNavigate } = {}) => {
  const { apiFetch } = useAuth();
  const toast = useToast();
  const [executiveData, setExecutiveData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState(7);
  const [zipLimit, setZipLimit] = useState(10);
  const [radiusMiles, setRadiusMiles] = useState(50);

  const fetchExecutiveData = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await apiFetch(
        `/demand/zip-nearby-executive?zip_limit=${zipLimit}&radius_miles=${radiusMiles}&days=${dateRange}`,
        { method: "GET" }
      );
      setExecutiveData(res.data);
      setIsLoading(false);
    } catch (e) {
      console.error(e);
      setIsLoading(false);
      toast.error("Failed to load geographic data");
    }
  }, [apiFetch, toast, dateRange, zipLimit, radiusMiles]);

  useEffect(() => {
    fetchExecutiveData();
  }, [fetchExecutiveData]);

  const getDemandColor = (level) => {
    const colors = {
      high: "#10b981",
      strong: "#3b82f6",
      emerging: "#f59e0b",
      low: "#6b7280"
    };
    return colors[level] || colors.low;
  };

  const getDemandBg = (level) => {
    const colors = {
      high: "#dcfce7",
      strong: "#dbeafe",
      emerging: "#fef3c7",
      low: "#f3f4f6"
    };
    return colors[level] || colors.low;
  };

  if (isLoading) {
    return <div className="page-container"><div className="loading-spinner">Loading geographic demand...</div></div>;
  }

  const dealerLocation = executiveData?.dealer_location;
  const nearbyZips = executiveData?.nearby_zips || [];
  const kpis = executiveData?.kpis || {};
  const appliedRadius = executiveData?.radius_miles || 50;

  // Prepare data for tables (Top 5 ZIPs for vehicles and price bands)
  const top5Zips = nearbyZips.slice(0, 5);

  // Calculate map center
  const mapCenter = dealerLocation?.lat 
    ? { lat: dealerLocation.lat, lng: dealerLocation.lng }
    : nearbyZips.length > 0 
      ? { lat: nearbyZips[0].lat, lng: nearbyZips[0].lng }
      : { lat: 39.8283, lng: -98.5795 };

  return (
    <div className="page-container geo-dashboard" data-testid="geo-demand-page">
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">Geographic Demand</h2>
          <p className="page-subtitle">
            {dealerLocation
              ? `Demand within ${appliedRadius} mi of ${dealerLocation.city || dealerLocation.zip}, ${dealerLocation.state || ''}`
              : "Set your dealership address in Settings to enable proximity-based demand analysis"}
          </p>
        </div>
      </div>

      <div className="geo-filters-bar" data-testid="geo-filters-bar">
        <div className="filter-group">
          <label>Radius</label>
          <StyledSelect value={radiusMiles} onChange={(e) => setRadiusMiles(Number(e.target.value))} testId="radius-select">
            <option value={25}>25 miles</option>
            <option value={50}>50 miles</option>
            <option value={100}>100 miles</option>
          </StyledSelect>
        </div>
        <div className="filter-group">
          <label>Show</label>
          <StyledSelect value={zipLimit} onChange={(e) => setZipLimit(Number(e.target.value))} testId="zip-limit-select">
            <option value={5}>Top 5</option>
            <option value={10}>Top 10</option>
            <option value={20}>Top 20</option>
          </StyledSelect>
        </div>
        <div className="filter-group">
          <label>Time</label>
          <StyledSelect value={dateRange} onChange={(e) => setDateRange(Number(e.target.value))} testId="date-range-select">
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </StyledSelect>
        </div>
      </div>

      {!dealerLocation && (
        <div className="geo-notice warning">
          <MapPin size={15} style={{flexShrink:0}} />
          <span>
            Set your dealership address in{" "}
            {/* iter88 — Issue 16: "Settings" is now a real link that
                jumps to the General Info page where the address fields
                live. Previously the word was bold-only and dealers had
                to hunt for the right page. */}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (onNavigate) onNavigate("general-info");
              }}
              className="text-blue-600 hover:text-blue-800 underline font-semibold"
              data-testid="geo-notice-settings-link"
            >
              General Info
            </a>{" "}
            to see demand ranked by distance from your location.
          </span>
        </div>
      )}

      {/* KPI Tiles Row — compact left-border style matching dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="kpi-tiles">
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center gap-3" style={{ borderLeft: '4px solid #2563EB', minHeight: 86, maxHeight: 120 }}>
          <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: '#2563EB15' }}>
            <MapPin size={18} style={{ color: '#2563EB' }} />
          </div>
          <div className="min-w-0">
            <p className="text-2xl font-bold text-gray-900 leading-tight tabular-nums">{kpis.nearby_zips_with_demand || 0}</p>
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mt-0.5">Nearby ZIPs</p>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center gap-3" style={{ borderLeft: '4px solid #F59E0B', minHeight: 86, maxHeight: 120 }}>
          <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: '#F59E0B15' }}>
            <Activity size={18} style={{ color: '#F59E0B' }} />
          </div>
          <div className="min-w-0">
            <p className="text-2xl font-bold text-gray-900 leading-tight tabular-nums">{(kpis.total_demand_signals || 0).toLocaleString()}</p>
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mt-0.5">Demand Signals</p>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center gap-3" style={{ borderLeft: '4px solid #10B981', minHeight: 86, maxHeight: 120 }}>
          <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: '#10B98115' }}>
            <ShieldCheck size={18} style={{ color: '#10B981' }} />
          </div>
          <div className="min-w-0">
            <p className="text-2xl font-bold text-gray-900 leading-tight tabular-nums">{kpis.verified_demand_pct || 0}%</p>
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mt-0.5">Verified Demand</p>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center gap-3" style={{ borderLeft: '4px solid #005CFF', minHeight: 86, maxHeight: 120 }}>
          <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: '#005CFF15' }}>
            <BarChart3 size={18} style={{ color: '#005CFF' }} />
          </div>
          <div className="min-w-0">
            {kpis.top_zip ? (
              <>
                <p className="text-xl font-bold text-gray-900 leading-tight tabular-nums">{kpis.top_zip.zip_code}</p>
                <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mt-0.5">
                  Top ZIP{kpis.top_zip.city ? ` · ${kpis.top_zip.city}` : ''}
                </p>
              </>
            ) : (
              <>
                <p className="text-2xl font-bold text-gray-900 leading-tight">—</p>
                <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mt-0.5">Top ZIP</p>
              </>
            )}
          </div>
        </div>
      </div>

      {nearbyZips.length === 0 ? (
        <div className="geo-empty-state">
          <div className="empty-state-icon"><MapPin size={24} /></div>
          <h3>No geographic demand data yet</h3>
          <p>ZIP-level demand will appear here once visitors interact with your listings.</p>
        </div>
      ) : (
        <>
          {/* Map + Ranked List Section */}
          <div className="geo-main-grid">
            {/* Map Panel (Location Context) */}
            <div className="geo-map-panel">
              <h3 className="panel-title">Location Context</h3>
              <div className="map-container-small">
                <MapContainer
                  center={[mapCenter.lat, mapCenter.lng]}
                  zoom={dealerLocation ? 8 : 5}
                  style={{ height: "320px", width: "100%", borderRadius: "8px" }}
                  scrollWheelZoom={false}
                >
                  <TileLayer
                    attribution='&copy; OpenStreetMap'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapCenterUpdater center={mapCenter} />
                  
                  {/* Dealer Location Marker */}
                  {dealerLocation && (
                    <CircleMarker
                      center={[dealerLocation.lat, dealerLocation.lng]}
                      radius={10}
                      pathOptions={{
                        fillColor: "#1a1a2e",
                        fillOpacity: 1,
                        color: "#fff",
                        weight: 2
                      }}
                    >
                      <Tooltip permanent direction="top" offset={[0, -8]}>
                        <span style={{fontSize: '11px', fontWeight: '600'}}>Your Dealership</span>
                      </Tooltip>
                    </CircleMarker>
                  )}
                  
                  {/* ZIP Markers - Simple circles sized by signal volume, colored by demand */}
                  {nearbyZips.slice(0, 10).map((zip, idx) => (
                    <CircleMarker
                      key={zip.zip_code}
                      center={[zip.lat, zip.lng]}
                      radius={Math.max(6, Math.min(18, 6 + Math.log(zip.total_demand_events + 1) * 3))}
                      pathOptions={{
                        fillColor: getDemandColor(zip.demand_level),
                        fillOpacity: 0.7,
                        color: getDemandColor(zip.demand_level),
                        weight: 1
                      }}
                    >
                      <Tooltip direction="top" offset={[0, -6]}>
                        <div style={{fontSize: '11px'}}>
                          <strong>{zip.zip_code}</strong>
                          {zip.city && <span> · {zip.city}</span>}
                          <br/>
                          {zip.distance_miles !== null && <span>{zip.distance_miles} mi · </span>}
                          {zip.demand_label}
                        </div>
                      </Tooltip>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </div>
            </div>

            {/* Ranked ZIP List Panel */}
            <div className="geo-list-panel">
              <h3 className="panel-title">Top Nearby ZIPs</h3>
              <table className="zip-table" data-testid="zip-ranked-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>ZIP</th>
                    <th>Distance</th>
                    <th>Signals</th>
                  </tr>
                </thead>
                <tbody>
                  {nearbyZips.map((zip, idx) => (
                    <tr key={zip.zip_code}>
                      <td className="rank-cell">{idx + 1}</td>
                      <td className="zip-cell">
                        <span className="zip-code">{zip.zip_code}</span>
                        {zip.city && <span className="zip-location">{zip.city}, {zip.state}</span>}
                      </td>
                      <td className="distance-cell">{zip.distance_miles !== null ? `${zip.distance_miles} mi` : '—'}</td>
                      <td className="signals-cell">
                        {zip.total_demand_events}
                        {zip.verified_demand_count > 0 && (
                          <span className="verified-indicator" title="Verified Demand">✓</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section: Most In-Demand Vehicles by ZIP */}
          <div className="geo-section">
            <h3 className="section-title">Top Vehicles by ZIP</h3>
            <p className="section-subtitle">Top 5 ZIPs × Top 3 vehicles</p>
            <table className="vehicles-by-zip-table" data-testid="vehicles-by-zip-table">
              <thead>
                <tr>
                  <th>ZIP</th>
                  <th>Location</th>
                  <th>Top Vehicles</th>
                </tr>
              </thead>
              <tbody>
                {top5Zips.map((zip) => {
                  const cityState = zip.city
                    ? `${zip.city}${zip.state ? `, ${zip.state}` : ""}`
                    : null;
                  return (
                  <tr key={zip.zip_code}>
                    <td className="zip-cell-compact">{zip.zip_code}</td>
                    <td className="location-cell" data-testid={`vbz-location-${zip.zip_code}`}>
                      {/* iter97 — Fix 1: ZIP + city/state stacked. Never show a bare dash. */}
                      {cityState ? (
                        <div className="zip-location-stacked">
                          <span className="zip-location-zip">{zip.zip_code}</span>
                          <span className="zip-location-city">{cityState}</span>
                        </div>
                      ) : (
                        <span className="zip-location-fallback">{zip.zip_code}</span>
                      )}
                    </td>
                    <td className="vehicles-cell">
                      {zip.top_vehicles && zip.top_vehicles.length > 0 ? (
                        <div className="vehicle-chips">
                          {zip.top_vehicles.map((v, vidx) => (
                            <span key={vidx} className="vehicle-chip">
                              {v.has_verified_identity && <span className="v-check">✓</span>}
                              {v.vehicle_year} {v.vehicle_make} {v.vehicle_model}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="no-data">No vehicle data</span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Section: Price Bands by ZIP */}
          <div className="geo-section">
            <h3 className="section-title">Price Bands by ZIP</h3>
            <p className="section-subtitle">Top 5 ZIPs with price-band breakdown</p>
            <table className="pricebands-by-zip-table" data-testid="pricebands-by-zip-table">
              <thead>
                <tr>
                  <th>ZIP</th>
                  <th>Location</th>
                  <th>Price Bands</th>
                </tr>
              </thead>
              <tbody>
                {top5Zips.map((zip) => {
                  const cityState = zip.city
                    ? `${zip.city}${zip.state ? `, ${zip.state}` : ""}`
                    : null;
                  return (
                  <tr key={zip.zip_code}>
                    <td className="zip-cell-compact">{zip.zip_code}</td>
                    <td className="location-cell" data-testid={`pbz-location-${zip.zip_code}`}>
                      {cityState ? (
                        <div className="zip-location-stacked">
                          <span className="zip-location-zip">{zip.zip_code}</span>
                          <span className="zip-location-city">{cityState}</span>
                        </div>
                      ) : (
                        <span className="zip-location-fallback">{zip.zip_code}</span>
                      )}
                    </td>
                    <td className="pricebands-cell">
                      {zip.price_bands && zip.price_bands.length > 0 ? (
                        <div className="priceband-chips">
                          {zip.price_bands.map((pb, pbidx) => (
                            <span key={pbidx} className="priceband-chip">
                              {pb.has_verified_identity && <span className="v-check">✓</span>}
                              {pb.price_band_label}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="no-data">No price band data</span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Compliance Note */}
          <div className="compliance-note">
            <strong>✓ Verified Demand</strong> = identity confirmed via verification · <strong>Anonymous Intent</strong> = signals without identity confirmation
          </div>
        </>
      )}
    </div>
  );
};

export default GeoDemandPage;
